import { useSyncExternalStore } from "react";

/**
 * Forhaandsvisningen i Ascendly: tekst som skrives, men ikke er lagret, og en
 * roed boks rundt det som redigeres.
 *
 * Samme fil ligger i alle kundesidene. Endres den, endres den overalt.
 *
 * To veier inn, fordi siden vises paa to maater:
 * - Paa ascendly.no ligger siden i en iframe. Dashbordet sender utkastet med
 *   postMessage, og siden svarer `ascendly:klar` naar den lytter.
 * - I iOS-appen ligger siden i en WebView, som ikke har noen forelder aa
 *   sende fra. Appen kaller `window.ascendlyInnhold(...)` i stedet, og siden
 *   sier fra gjennom `ReactNativeWebView.postMessage`.
 *
 * Foer dette fikk rammen ny adresse (`?forhandsvis=`) for hvert tastetrykk,
 * lastet paa nytt og hoppet til toppen av siden. Adressen virker fortsatt
 * som reserve, se `useUtkast`.
 *
 * Ingenting her lagres eller sendes videre. Utkastet lever bare i denne ene
 * visningen, og en side som aapnes vanlig merker ingenting av det.
 */

type Melding = {
  type?: string;
  utkast?: Record<string, string>;
  /** Noekkelen som redigeres. */
  aktiv?: string | null;
  /** Det feltet viser akkurat naa: utkast, lagret tekst, bilde eller standard. */
  aktivVerdi?: string | null;
};

let utkast: Map<string, string> | null = null;
let aktiv: string | null = null;
let aktivVerdi: string | null = null;
const lyttere = new Set<() => void>();

// Bare Ascendly faar styre forhaandsvisningen i en iframe. I appen er det
// ingen avsender aa sjekke: bare den som eier WebView-en kan kalle funksjonen.
const TILLATTE_OPPHAV = [
  /^https:\/\/(www\.)?ascendly\.no$/,
  /^https:\/\/[a-z0-9-]+\.lovable\.app$/,
  /^https:\/\/[a-z0-9-]+\.lovableproject\.com$/,
  /^http:\/\/localhost(:\d+)?$/,
];

function motta(d: Melding) {
  if (d?.type !== "ascendly:innhold") return;
  utkast = new Map(Object.entries(d.utkast ?? {}));
  const flytt = (d.aktiv ?? null) !== aktiv;
  aktiv = d.aktiv ?? null;
  aktivVerdi = d.aktivVerdi ?? null;
  lyttere.forEach((l) => l());
  // Etter at React har tegnet teksten, og en gang til for sider som bruker
  // litt tid paa aa tegne om (bilder, animasjoner som toner inn).
  setTimeout(() => {
    marker(flytt);
    merkSkjulte();
  }, 80);
  setTimeout(() => {
    marker(false);
    merkSkjulte();
  }, 600);
}

function installer() {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    __ascendlyForhandsvisning?: boolean;
    ascendlyInnhold?: (d: Melding) => void;
    ReactNativeWebView?: { postMessage: (s: string) => void };
  };
  if (w.__ascendlyForhandsvisning) return;
  w.__ascendlyForhandsvisning = true;

  if (window.parent !== window) {
    window.addEventListener("message", (e: MessageEvent) => {
      if (!TILLATTE_OPPHAV.some((r) => r.test(e.origin))) return;
      const d = e.data as Melding;
      // Redigeringen starter foerst naar et tillatt opphav har snakket med oss,
      // og svarene sendes bare dit.
      if (onskerRedigering() && !forelder) {
        forelder = e.origin;
        startRedigering();
      }
      if (d?.type === "ascendly:velg") velgElement(e.data as Velg);
      else if (d?.type === "ascendly:slutt-velg") sluttVelg();
      else motta(d);
    });
    window.parent.postMessage({ type: "ascendly:klar" }, "*");
  }

  if (w.ReactNativeWebView) {
    w.ascendlyInnhold = (d) => motta({ ...d, type: "ascendly:innhold" });
    w.ReactNativeWebView.postMessage(JSON.stringify({ type: "ascendly:klar" }));
  }
}

installer();

const MERKE = "data-ascendly-aktiv";

function normaliser(s: string) {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Om elementet faktisk vises. Mange sider har samme tekst to ganger, én
 * utgave for mobil og én for PC, og den ene er skjult. Arena sin overskrift
 * sto foerst i den skjulte mobilutgaven, og boksen ble tegnet rundt noe
 * ingen kunne se.
 */
function vises(el: Element): boolean {
  return el.getClientRects().length > 0;
}

/** Det feltet viser, som tekst paa siden: uten *utheving* og uten {plassholdere}. */
function soeketekst(verdi: string) {
  return normaliser((verdi.split("{")[0] ?? "").replace(/\*/g, "")).slice(0, 60);
}

/** Bilde eller film etter adressen, tekst etter ordene. */
function finnElement(verdi: string): HTMLElement | null {
  if (/^(https?:|\/)/.test(verdi)) {
    const media = Array.from(document.querySelectorAll<HTMLElement>("img, video, source")).find((el) => {
      const src = el.getAttribute("src") ?? "";
      const synlig = el.tagName === "SOURCE" ? el.parentElement && vises(el.parentElement) : vises(el);
      return synlig && src && (src === verdi || verdi.endsWith(src) || src.endsWith(verdi));
    });
    if (media) return media.tagName === "SOURCE" ? media.parentElement : media;
    // Bakgrunnsbilder staar i style, ikke i src.
    const bak = Array.from(document.querySelectorAll<HTMLElement>("[style*='background']")).find(
      (el) => vises(el) && (el.getAttribute("style") ?? "").includes(verdi),
    );
    if (bak) return bak;
    return null;
  }
  const soek = soeketekst(verdi);
  if (soek.length < 2) return null;
  const vandrer = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let reserve: HTMLElement | null = null;
  for (let n = vandrer.nextNode(); n; n = vandrer.nextNode()) {
    const el = n.parentElement;
    if (!el || el.closest("script, style, noscript, [aria-hidden='true']") || !vises(el)) continue;
    if (normaliser(n.textContent ?? "").includes(soek)) return el;
    // Tekst delt paa flere noder (lenker, <br>, utheving): se paa forelderen.
    if (!reserve && el.parentElement && normaliser(el.parentElement.textContent ?? "").includes(soek)) {
      reserve = el.parentElement;
    }
  }
  return reserve;
}

function marker(flytt: boolean) {
  if (typeof document === "undefined") return;
  if (!document.getElementById("ascendly-markering")) {
    const stil = document.createElement("style");
    stil.id = "ascendly-markering";
    stil.textContent = `[${MERKE}]{outline:3px solid #ef4444 !important;outline-offset:4px;border-radius:4px}`;
    document.head.appendChild(stil);
  }
  document.querySelectorAll(`[${MERKE}]`).forEach((el) => el.removeAttribute(MERKE));
  if (!aktiv) return;
  const verdi = utkast?.get(aktiv) || aktivVerdi || "";
  const el = verdi ? finnElement(verdi) : null;
  if (!el) return;
  el.setAttribute(MERKE, "");
  if (flytt) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

function abonner(l: () => void) {
  lyttere.add(l);
  return () => lyttere.delete(l);
}

/**
 * Utkastet som gjelder: det Ascendly har sendt som melding, ellers det som
 * staar i adressen. Kalles oeverst i hooken som leser teksten, i stedet for
 * `utkastFraAdressen()` direkte.
 */
export function useUtkast(fraAdressen: () => Map<string, string>): Map<string, string> {
  const fraMelding = useSyncExternalStore(
    abonner,
    () => utkast,
    () => null,
  );
  return fraMelding ?? fraAdressen();
}

// ---------------------------------------------------------------------------
// Redigering i Ascendly (fra 16.10.2026)
//
// Med ?rediger=1 i adressen, i en ramme fra et tillatt opphav, blir siden
// redigerbar: blaa ramme under musen, klikk velger, og tekst kan skrives rett
// paa siden. Klikk navigerer ikke; sidene byttes fra Ascendly. Uten ?rediger=1
// skjer ingenting av dette. Meldingene er beskrevet i kunde-oversikt,
// docs/superpowers/plans/2026-10-16-visuell-redigering.md (Task 7).
// ---------------------------------------------------------------------------

type Velg = { nokkel: string; verdi: string; redigerbar: boolean; maks: number | null; flerlinje: boolean };

let forelder: string | null = null;
let sistKlikket: HTMLElement | null = null;
let valgtEl: HTMLElement | null = null;
let valgtNokkel: string | null = null;
// Teksten da feltet ble valgt. «ferdig» sendes bare naar den er endret: ellers
// lagret et bekreftelsesvindu (som tar fokus) gammel tekst paa nytt.
let startTekst = "";

const VALGT = "data-ascendly-valgt";
const UI = "ascendly-ui";

function onskerRedigering(): boolean {
  if (typeof window === "undefined" || window.parent === window) return false;
  return new URLSearchParams(window.location.search).get("rediger") === "1";
}

/** Sant i redigeringen: da vises skjulte seksjoner (gjennomsiktige) saa de kan vises igjen. */
export function iRedigering(): boolean {
  return onskerRedigering();
}

function tilForelder(m: Record<string, unknown>) {
  if (forelder) window.parent.postMessage(m, forelder);
}

/**
 * Hvordan siden skriver fargene sine. «hex» (og oklch/hsl()-sider): verdien
 * settes rett inn. «hsl»: siden bruker `hsl(var(--primary))` med bare tallene
 * i variabelen («0 85% 38%»), saa hex-fargen gjoeres om til de tallene.
 */
export type Fargeformat = "hex" | "hsl";

function hexTilHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const maks = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (maks + min) / 2;
  let h = 0;
  let s = 0;
  if (maks !== min) {
    const d = maks - min;
    s = l > 0.5 ? d / (2 - maks - min) : d / (maks + min);
    h = maks === r ? (g - b) / d + (g < b ? 6 : 0) : maks === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** CSS for merkefargene: `:root{--primary:#c0392b}`. Tom streng naar ingen farge er satt. */
export function temaCss(farger: [string[], string, Fargeformat?][]): string {
  const regler = farger.flatMap(([vars, verdi, format]) =>
    /^#[0-9a-f]{6}$/i.test(verdi)
      ? vars
          .filter((v) => /^--[a-z0-9-]+$/i.test(v))
          .map((v) => `${v}:${format === "hsl" ? hexTilHsl(verdi) : verdi}`)
      : [],
  );
  return regler.length ? `:root{${regler.join(";")}}` : "";
}

function merkSkjulte() {
  if (typeof document === "undefined") return;
  document.querySelectorAll<HTMLElement>("[data-ascendly-seksjon]").forEach((el) => {
    const n = el.getAttribute("data-ascendly-seksjon") ?? "";
    if (utkast?.get(n) === "nei") el.setAttribute("data-ascendly-skjult", "");
    else el.removeAttribute("data-ascendly-skjult");
  });
}

function iUi(el: Element | null) {
  return Boolean(el && (el as HTMLElement).closest?.(`#${UI}, #${UI}-knapp`));
}

/** Naermeste element med egen tekst, opptil seks nivaaer opp. */
function tekstElement(el: Element | null): HTMLElement | null {
  let x = el as HTMLElement | null;
  for (let i = 0; x && i < 6; x = x.parentElement, i++) {
    if (x === document.body) return null;
    const harTekst = Array.from(x.childNodes).some((n) => n.nodeType === 3 && (n.textContent ?? "").trim().length > 1);
    if (harTekst) return x;
  }
  return null;
}

function bildeAdresse(el: HTMLElement): string | null {
  if (el instanceof HTMLImageElement) return el.currentSrc || el.src || null;
  if (el instanceof HTMLVideoElement) return el.currentSrc || el.querySelector("source")?.src || el.src || null;
  const m = /url\(["']?([^"')]+)["']?\)/.exec(getComputedStyle(el).backgroundImage);
  return m ? m[1]! : null;
}

/** Det som kan velges under musen: tekst foerst, saa bilde/film, saa bakgrunnsbilde. */
function maal(el: Element | null): { el: HTMLElement; bilde: boolean } | null {
  if (!el || iUi(el)) return null;
  const tekst = tekstElement(el);
  if (tekst) return { el: tekst, bilde: false };
  const media = (el as HTMLElement).closest?.("img, video") as HTMLElement | null;
  if (media) return { el: media, bilde: true };
  let x = el as HTMLElement | null;
  for (let i = 0; x && i < 5; x = x.parentElement, i++) {
    if (x === document.body) break;
    if (getComputedStyle(x).backgroundImage.includes("url(")) return { el: x, bilde: true };
  }
  return null;
}

function lagUi() {
  if (document.getElementById(UI)) return;
  const stil = document.createElement("style");
  stil.textContent = [
    `#${UI}{position:fixed;pointer-events:none;z-index:2147483646;border:2px solid #3b82f6;border-radius:4px;display:none}`,
    `#${UI}-knapp{position:fixed;z-index:2147483647;display:none;font:600 13px system-ui,sans-serif;background:#111;color:#fff;border:0;border-radius:999px;padding:7px 12px;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,.3)}`,
    `[${VALGT}]{outline:2px solid #2563eb !important;outline-offset:3px;border-radius:3px}`,
    `[${VALGT}][contenteditable]{background:rgba(37,99,235,.08);cursor:text}`,
    `[data-ascendly-skjult]{opacity:.35 !important}`,
  ].join("\n");
  document.head.appendChild(stil);
  const boks = document.createElement("div");
  boks.id = UI;
  document.body.appendChild(boks);
  const knapp = document.createElement("button");
  knapp.id = `${UI}-knapp`;
  knapp.type = "button";
  document.body.appendChild(knapp);
}

function visBoks(el: HTMLElement | null) {
  const boks = document.getElementById(UI);
  if (!boks) return;
  if (!el) {
    boks.style.display = "none";
    return;
  }
  const r = el.getBoundingClientRect();
  Object.assign(boks.style, {
    display: "block",
    left: `${r.left - 3}px`,
    top: `${r.top - 3}px`,
    width: `${r.width + 6}px`,
    height: `${r.height + 6}px`,
  });
}

function visSeksjonsknapp(seksjon: HTMLElement | null) {
  const knapp = document.getElementById(`${UI}-knapp`) as HTMLButtonElement | null;
  if (!knapp) return;
  if (!seksjon) {
    knapp.style.display = "none";
    return;
  }
  const r = seksjon.getBoundingClientRect();
  knapp.textContent = seksjon.hasAttribute("data-ascendly-skjult") ? "Vis seksjonen" : "Skjul seksjonen";
  knapp.dataset["nokkel"] = seksjon.getAttribute("data-ascendly-seksjon") ?? "";
  Object.assign(knapp.style, {
    display: "block",
    top: `${Math.max(8, r.top + 12)}px`,
    left: `${Math.max(8, Math.min(window.innerWidth - 150, r.right - 150))}px`,
  });
}

function sluttVelg() {
  if (valgtEl) {
    valgtEl.removeAttribute(VALGT);
    valgtEl.removeAttribute("contenteditable");
    valgtEl.oninput = null;
    valgtEl.onkeydown = null;
    valgtEl.onblur = null;
    valgtEl.onbeforeinput = null;
  }
  valgtEl = null;
  valgtNokkel = null;
}

function avsluttSkriving() {
  if (!valgtEl || !valgtNokkel || !valgtEl.hasAttribute("contenteditable")) return;
  const tekst = valgtEl.innerText.replace(/\u00a0/g, " ");
  valgtEl.removeAttribute("contenteditable");
  if (tekst !== startTekst) tilForelder({ type: "ascendly:ferdig", nokkel: valgtNokkel, tekst });
}

/**
 * Elementet som ble klikket, eller en av de fire naermeste foreldrene, som
 * inneholder hele teksten til feltet. Overskrifter med ett <span> per ord
 * (WordFade og lignende) traff ellers et enkeltord, og soeket i resten av
 * siden kunne finne en helt annen tekst, som da ble den eieren skrev i.
 */
function rundtKlikket(soek: string): HTMLElement | null {
  if (!sistKlikket || !soek) return null;
  let x: HTMLElement | null = sistKlikket;
  for (let i = 0; x && i < 5 && x !== document.body; x = x.parentElement, i++) {
    if (normaliser(x.innerText).includes(soek)) return x;
  }
  return null;
}

function velgElement(v: Velg) {
  avsluttSkriving();
  sluttVelg();
  const soek = soeketekst(v.verdi);
  const el =
    rundtKlikket(soek) ?? finnElement(v.verdi);
  if (!el) {
    tilForelder({ type: "ascendly:ikke-funnet", nokkel: v.nokkel });
    return;
  }
  valgtEl = el;
  valgtNokkel = v.nokkel;
  el.setAttribute(VALGT, "");
  // Valgt fra panelet (ikke ved klikk paa siden): rull dit.
  if (!sistKlikket || !el.contains(sistKlikket)) el.scrollIntoView({ behavior: "smooth", block: "center" });
  if (!v.redigerbar) return;
  el.setAttribute("contenteditable", "plaintext-only");
  startTekst = el.innerText.replace(/\u00a0/g, " ");
  el.focus();
  const sel = window.getSelection();
  if (sel) {
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);
  }
  el.oninput = () => tilForelder({ type: "ascendly:skriv", nokkel: v.nokkel, tekst: el.innerText.replace(/\u00a0/g, " ") });
  el.onkeydown = (e) => {
    if (e.key === "Escape" || (e.key === "Enter" && !v.flerlinje)) {
      e.preventDefault();
      avsluttSkriving();
    }
  };
  el.onbeforeinput = (e) => {
    if (v.maks && e.inputType.startsWith("insert") && el.innerText.length >= v.maks) e.preventDefault();
  };
  el.onblur = () => avsluttSkriving();
}

/**
 * Hvor paa siden klikket var: menyen, bunnen eller innholdet. Ascendly bruker
 * det naar samme tekst staar flere steder («Villas» i menyen og som overskrift
 * paa soekesidene hos JK). En <header> med sidens h1 er toppbildet, ikke menyen.
 */
function omraade(el: HTMLElement): "topp" | "bunn" | "innhold" {
  if (el.closest("nav, [role='navigation']")) return "topp";
  const hode = el.closest("header, [role='banner']");
  if (hode && !hode.querySelector("h1")) return "topp";
  if (el.closest("footer, [role='contentinfo']")) return "bunn";
  return "innhold";
}

function startRedigering() {
  lagUi();
  let seksjonUnder: HTMLElement | null = null;
  document.addEventListener(
    "mousemove",
    (e) => {
      const mal = e.target as Element;
      const m = maal(mal);
      visBoks(m && m.el !== valgtEl ? m.el : null);
      const s = (mal as HTMLElement).closest?.("[data-ascendly-seksjon]") as HTMLElement | null;
      if (s || !iUi(mal)) seksjonUnder = s;
      visSeksjonsknapp(seksjonUnder);
    },
    true,
  );
  window.addEventListener("scroll", () => visBoks(null), true);
  document.addEventListener(
    "click",
    (e) => {
      const mal = e.target as HTMLElement;
      if (mal.id === `${UI}-knapp`) {
        e.preventDefault();
        e.stopPropagation();
        tilForelder({ type: "ascendly:seksjon", nokkel: mal.dataset["nokkel"] });
        return;
      }
      // Ingen lenker, knapper eller skjemaer i redigeringen.
      e.preventDefault();
      e.stopPropagation();
      if (valgtEl && valgtEl.contains(mal) && valgtEl.hasAttribute("contenteditable")) return;
      const m = maal(mal);
      if (!m) return;
      sistKlikket = m.el;
      const tekster: string[] = [];
      if (!m.bilde) {
        for (let x: HTMLElement | null = m.el, i = 0; x && i < 3 && x !== document.body; x = x.parentElement, i++) {
          const tekst = x.innerText.replace(/\s+/g, " ").trim().slice(0, 400);
          if (tekst && !tekster.includes(tekst)) tekster.push(tekst);
        }
      }
      const seksjon =
        (mal.closest("[data-ascendly-seksjon]") as HTMLElement | null)?.getAttribute("data-ascendly-seksjon") ?? null;
      tilForelder({ type: "ascendly:klikk", tekster, src: m.bilde ? bildeAdresse(m.el) : null, seksjon, omraade: omraade(mal) });
    },
    true,
  );
  document.addEventListener("submit", (e) => e.preventDefault(), true);
}
